import { writeRefusedMessage } from '@ups/xplat/utils';

/**
 * Task 34979: which failed offline replays leave the queue. A replay pass runs only on app init and on
 * reconnect, so three permanent failures means three separate sessions or reconnects: enough for a token
 * refresh, a deploy-time 404 or a permission restore to clear, and still a bound on retrying forever.
 */
export const OFFLINE_REPLAY_MAX_PERMANENT_FAILURES = 3;

/** catchError marker for a replay that failed for any reason other than the stage gate's writeRefused 403. */
export interface IReplayFailed {
  replayFailed: true;
  status: number;
  error: unknown;
}

/** The marker for a failed replay, from the HttpErrorResponse (status 0 when there is none). */
export function replayFailedFrom(err: unknown): IReplayFailed {
  const e = err as { status?: number; error?: unknown } | null;
  return { replayFailed: true, status: e?.status ?? 0, error: e?.error };
}

/**
 * True when retrying cannot fix the failure: a 4xx other than 401 (token refresh), 408 (timeout) and 429
 * (rate limit). Offline (0) and 5xx keep keep-and-retry.
 */
export function isPermanentReplayFailure(status: number | undefined): boolean {
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    status !== 401 &&
    status !== 408 &&
    status !== 429
  );
}

/** The server's own words for a failure, if it sent any: a string body, or message/title/detail. */
export function replayErrorText(error: unknown): string {
  let text = '';
  if (typeof error === 'string') {
    text = error;
  } else if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    const found = [e['message'], e['Message'], e['detail'], e['title']].find(
      (v) => typeof v === 'string' && v.trim()
    );
    text = (found as string) ?? '';
  }
  text = text.replace(/\s+/g, ' ').trim();
  return text.length > 150 ? `${text.slice(0, 147)}...` : text;
}

/**
 * A JSON object body: the API itself refused. A string body (an App Service IP-restriction or WAF HTML page, a
 * captive portal) can be transient, for example after reconnecting off the VPN, so it does not count.
 */
function hasRefusalBody(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    !Array.isArray(error) &&
    Object.keys(error).length > 0
  );
}

/**
 * True when a permanently failed replay leaves the queue now: at once for a 403 whose body is a JSON object (an
 * explicit refusal from the API; a stale token is a 401 or a bare 403), else on its Nth permanent failure.
 */
export function shouldDropReplay(
  status: number,
  error: unknown,
  priorPermanentFailures: number
): boolean {
  if (!isPermanentReplayFailure(status)) {
    return false;
  }
  return (
    (status === 403 && hasRefusalBody(error)) ||
    priorPermanentFailures + 1 >= OFFLINE_REPLAY_MAX_PERMANENT_FAILURES
  );
}

/** The one-time server log reason for a dropped replay. */
export function droppedReplayReason(
  status: number,
  error: unknown,
  attempts: number
): string {
  const text = replayErrorText(error);
  return `Dropped after ${attempts} attempt${attempts === 1 ? '' : 's'}: HTTP ${status}${text ? ` ${text}` : ''}`;
}

/** Which offline change it was, for the user: the method and the API path (no host, no query). */
export function droppedReplayLabel(
  item: { method: string; url: string },
  status: number,
  error: unknown
): string {
  const path = `${item.url ?? ''}`
    .replace(/^[a-z]+:\/\/[^/]+/i, '')
    .split('?')[0];
  const text = replayErrorText(error);
  return `${item.method} ${path || '/'} (HTTP ${status}${text ? `: ${text}` : ''})`;
}

/**
 * The single alert for one queue pass (Task 34976 shape, extended by Task 34979): the gate-refused controls,
 * then each change that was dropped. Null when there is nothing to tell.
 */
export function offlineReplayAlertText(
  refusedControls: string[],
  droppedLabels: string[]
): string | null {
  const parts: string[] = [];
  if (refusedControls.length) {
    parts.push(
      `A change you made offline was not fully saved. ${writeRefusedMessage(refusedControls)}`
    );
  }
  if (droppedLabels.length) {
    parts.push(
      `${droppedLabels.length === 1 ? 'A change' : `${droppedLabels.length} changes`} you made offline could not be saved and ${droppedLabels.length === 1 ? 'was' : 'were'} removed from the offline queue: ${droppedLabels.join('; ')}.`
    );
  }
  return parts.length ? parts.join('\n\n') : null;
}
